import { useEffect, useState, useRef } from 'react';
import { MarkdownRenderer } from '@/components/ui/markdown-renderer';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePublicChat } from '@/hooks/usePublicChat';
import { supabase } from '@/integrations/backend/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SeoHead } from '@/components/seo/SeoHead';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Bot, Loader2, Send, AlertCircle, UserCircle2, Mail, PlusCircle, X, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { PageSettings } from '@/types/editor';
import { getBackgroundStyle, getPresetAnimationClass, getVideoOverlayStyle } from '@/lib/background';
import { getGlobalButtonStyle, resolveButtonColors } from '@/lib/linkStyle';
import { getLastBioTheme, getInstantBg } from '@/lib/bioThemeCache';
import { cn } from '@/lib/utils';
import LiquidIridescentBackground from '@/components/background/LiquidIridescentBackground';
import WebGLIridescentBackground from '@/components/background/WebGLIridescentBackground';
import WebGLAuroraBorealisBackground from '@/components/background/WebGLAuroraBorealisBackground';
import WebGLMeshGradientBackground from '@/components/background/WebGLMeshGradientBackground';
import WebGLLiquidMarbleBackground from '@/components/background/WebGLLiquidMarbleBackground';
import WebGLPortalBackground from '@/components/background/WebGLPortalBackground';
import WebGLPoolBackground from '@/components/background/WebGLPoolBackground';
import WebGLCyberfluidBackground from '@/components/background/WebGLCyberfluidBackground';
import VideoBackground from '@/components/background/VideoBackground';

export default function PublicChat() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();

  const [agentName, setAgentName] = useState('AI Assistant');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [agentValid, setAgentValid] = useState<boolean | null>(null);
  const [ownerUsername, setOwnerUsername] = useState<string | null>(null);
  const [settings, setSettings] = useState<PageSettings | null>(null);
  const [input, setInput] = useState('');
  const [requireVisitorInfo, setRequireVisitorInfo] = useState(false);
  const [inactivityTimeoutMinutes, setInactivityTimeoutMinutes] = useState(30);
  const [visitorName, setVisitorName] = useState('');
  const [visitorEmail, setVisitorEmail] = useState('');
  const [chatStarted, setChatStarted] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [ownerDisplayName, setOwnerDisplayName] = useState<string | null>(null);
  const [ownerAvatarUrl, setOwnerAvatarUrl] = useState<string | null>(null);
  const [ownerBio, setOwnerBio] = useState<string | null>(null);
  const [ownerAvatarError, setOwnerAvatarError] = useState(false);
  const [searchParams] = useSearchParams();
  const embedMode = searchParams.get('embed') === '1';
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { messages, isLoading, error, isLocked, sendMessage, startNewConversation } = usePublicChat(
    agentValid ? agentId! : null,
    {
      visitorName: chatStarted ? visitorName : undefined,
      visitorEmail: chatStarted ? visitorEmail : undefined,
      inactivityTimeoutMinutes,
    },
  );

  // Validate agent and load theme
  useEffect(() => {
    if (!agentId) { setAgentValid(false); return; }

    supabase
      .from('chat_agents')
      .select('agent_name, welcome_message, is_enabled, user_id')
      .eq('id', agentId)
      .eq('is_enabled', true)
      .maybeSingle()
      .then(async ({ data }) => {
        if (!data) { setAgentValid(false); return; }

        setAgentName(data.agent_name || 'AI Assistant');
        setWelcomeMessage(data.welcome_message || '');
        const userId = data.user_id;
        setAgentValid(true);

        // Fetch extra agent fields separately to avoid deep type instantiation
        type AgentExtra = { require_visitor_info: boolean | null; inactivity_timeout_minutes: number | null };
        const agentExtraRes = await supabase.from('chat_agents').select('require_visitor_info, inactivity_timeout_minutes').eq('id', agentId!).maybeSingle();
        if (agentExtraRes.data) {
          const extra = agentExtraRes.data as unknown as AgentExtra;
          setRequireVisitorInfo(extra.require_visitor_info ?? false);
          setInactivityTimeoutMinutes(extra.inactivity_timeout_minutes ?? 30);
        }

        // Fetch owner username + page settings
        type ProfileRow = { username: string | null; display_name: string | null; avatar_url: string | null; bio: string | null };
        type SettingsRow = { background_type: string | null; background_color: string | null; background_gradient_start: string | null; background_gradient_end: string | null; background_image_url: string | null; background_video_url: string | null; background_video_muted: boolean | null; background_video_loop: boolean | null; background_video_fit: string | null; background_overlay_enabled: boolean | null; background_overlay_color: string | null; background_overlay_opacity: number | null; background_overlay_source: string | null; background_mode: string | null; theme: string | null; button_color: string | null; button_text_color: string | null; text_color: string | null; font_family: string | null };
        const profileRes = await supabase.from('profiles').select('username, display_name, avatar_url, bio').eq('user_id', userId).maybeSingle();
        const settingsRes = await supabase.from('page_settings').select('background_type, background_color, background_gradient_start, background_gradient_end, background_image_url, background_video_url, background_video_muted, background_video_loop, background_video_fit, background_overlay_enabled, background_overlay_color, background_overlay_opacity, background_overlay_source, background_mode, theme, button_color, button_text_color, text_color, font_family').eq('user_id', userId).maybeSingle();
        const profile = profileRes.data as ProfileRow | null;
        const settings = settingsRes.data as SettingsRow | null;
        if (profile?.username) setOwnerUsername(profile.username);
        if (profile?.display_name) setOwnerDisplayName(profile.display_name);
        if (profile?.avatar_url) setOwnerAvatarUrl(profile.avatar_url);
        if (profile?.bio) setOwnerBio(profile.bio);
        if (settings) setSettings(settings as unknown as PageSettings);
      });
  }, [agentId]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = () => {
    if (!input.trim() || isLoading || isLocked) return;
    sendMessage(input);
    setInput('');
  };

  const handleStartChat = (e: React.FormEvent) => {
    e.preventDefault();
    if (!visitorName.trim()) return;
    setChatStarted(true);
  };

  const handleConfirmEnd = () => {
    startNewConversation();
    if (ownerUsername) navigate(`/${ownerUsername}`);
    else navigate('/');
  };

  // ── Theme helpers ───────────────────────────────────────────────────────────
  const bgStyle = settings ? getBackgroundStyle(settings) : undefined;
  const presetAnim = settings ? getPresetAnimationClass(settings) : null;
  const isLiquid    = presetAnim === 'liquid-iridescent-bg';
  const isWebGLIrid = presetAnim === 'webgl-iridescent-bg';
  const isAurora    = presetAnim === 'webgl-aurora-borealis-bg';
  const isPortal    = presetAnim === 'webgl-portal';
  const isPool      = presetAnim === 'webgl-pool';
  const isCyber     = presetAnim === 'webgl-cyber-fluid';
  const isMarble    = presetAnim?.startsWith('webgl-liquid-') || false;
  const isMesh      = presetAnim?.startsWith('webgl-mesh-') || false;
  const meshVariant = isMesh ? presetAnim?.replace('webgl-mesh-', '') : undefined;
  const marbleVariant = isMarble ? presetAnim?.replace('webgl-liquid-', '') : undefined;
  const isVideo = settings?.background_mode === 'custom' && !!settings.background_video_url;
  const showCssBg = !isLiquid && !isWebGLIrid && !isAurora && !isPortal && !isPool && !isCyber && !isMarble && !isMesh;

  const { btnBg, btnText } = settings ? resolveButtonColors(settings) : { btnBg: undefined, btnText: undefined };

  // Dark/light axis — light text means dark-background theme, dark text means light-background theme
  const isDark = (() => {
    const hex = (settings?.text_color ?? '#ffffff').replace('#', '');
    if (hex.length < 6) return true;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return (r * 0.299 + g * 0.587 + b * 0.114) > 128;
  })();

  // Glass header/footer — opaque enough to clearly separate from the background
  // and always readable regardless of what animated/gradient content is behind them
  const glassBg = settings
    ? (isDark ? 'rgba(0,0,0,0.68)' : 'rgba(255,255,255,0.88)')
    : undefined;
  // Divider lines between header/content and content/footer
  const glassDivider = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)';

  const glassBase: React.CSSProperties = {
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    backgroundColor: glassBg,
    color: settings?.text_color,
  };
  const glassHeader: React.CSSProperties = {
    ...glassBase,
    borderBottom: settings ? `1px solid ${glassDivider}` : undefined,
  };
  const glassFooter: React.CSSProperties = {
    ...glassBase,
    borderTop: settings ? `1px solid ${glassDivider}` : undefined,
  };

  // Assistant bubbles — always contrast against whatever background is behind them.
  // Dark themes (colorful/dark bg + light text): dark overlay so white text always reads.
  // Light themes (light bg + dark text): very subtle tint preserving theme feel.
  const assistantBubble: React.CSSProperties = settings
    ? {
        background: isDark ? 'rgba(0,0,0,0.42)' : 'rgba(0,0,0,0.07)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        color: settings.text_color,
      }
    : {};

  const userBubbleStyle = settings ? getGlobalButtonStyle(settings) : undefined;
  const sendBtnStyle = settings ? { background: btnBg, color: btnText, border: 'none' } : undefined;
  const textStyle: React.CSSProperties = settings ? { color: settings.text_color } : {};

  // Input field — clearly visible within the glass footer on any theme
  const inputStyle: React.CSSProperties = settings
    ? {
        color: settings.text_color,
        background: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.07)',
        borderColor: isDark ? 'rgba(255,255,255,0.28)' : 'rgba(0,0,0,0.20)',
        caretColor: settings.text_color,
      }
    : {};

  // ── Loading / invalid states ────────────────────────────────────────────────
  if (agentValid === null) {
    const cached = getLastBioTheme();
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.12 } }}
        transition={{ duration: 0.22, ease: 'easeOut', delay: 0.15 }}
        className="min-h-screen flex items-center justify-center"
        style={cached ? getInstantBg(cached.settings) : { background: 'hsl(var(--background))' }}
      >
        <Loader2 className="h-8 w-8 animate-spin motion-reduce:animate-none" style={cached ? { color: cached.settings.text_color } : {}} />
      </motion.div>
    );
  }

  if (!agentValid) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <SeoHead title="Chat unavailable" description="This chat agent is not available." path={`/chat/${agentId}`} noindex />
        <div className="text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto" />
          <h1 className="text-xl font-semibold">Chat unavailable</h1>
          <p className="text-muted-foreground">This chat agent is not available or has been disabled.</p>
        </div>
      </div>
    );
  }

  // ── Pre-chat form ───────────────────────────────────────────────────────────
  if (requireVisitorInfo && !chatStarted) {
    return (
      <div className="relative min-h-screen overflow-hidden flex items-center justify-center p-4">
        <SeoHead title={`Chat with ${agentName}`} description={`Chat with ${agentName} powered by AI`} path={`/chat/${agentId}`} noindex />

        {/* Background (same as main chat) */}
        {isLiquid && <div className="absolute inset-0 -z-10"><LiquidIridescentBackground /></div>}
        {isWebGLIrid && <div className="absolute inset-0 -z-10"><WebGLIridescentBackground /></div>}
        {isAurora && <div className="absolute inset-0 -z-10"><WebGLAuroraBorealisBackground /></div>}
        {isPortal && <div className="absolute inset-0 -z-10"><WebGLPortalBackground /></div>}
        {isPool && <div className="absolute inset-0 -z-10"><WebGLPoolBackground /></div>}
        {isCyber && <div className="absolute inset-0 -z-10"><WebGLCyberfluidBackground /></div>}
        {isMarble && <div className="absolute inset-0 -z-10"><WebGLLiquidMarbleBackground variant={marbleVariant} /></div>}
        {isMesh && <div className="absolute inset-0 -z-10"><WebGLMeshGradientBackground variant={meshVariant} /></div>}
        {isVideo && settings?.background_video_url && (
          <div className="absolute inset-0 -z-10">
            <VideoBackground url={settings.background_video_url} />
            {settings.background_overlay_enabled && (
              <div className="absolute inset-0" style={{ background: getVideoOverlayStyle(settings) ?? undefined }} />
            )}
          </div>
        )}
        {showCssBg && settings && (
          <div className={`absolute inset-0 -z-10 ${presetAnim ?? ''}`} style={{ background: bgStyle }} />
        )}

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl shadow-2xl"
          style={settings ? { ...glassBase, border: `1px solid ${glassDivider}` } : { background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}
        >
          {/* Form header */}
          <div className="px-6 py-5 flex items-center gap-3" style={settings ? { borderBottom: `1px solid ${glassDivider}` } : { borderBottom: '1px solid hsl(var(--border))' }}>
            <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0"
              style={settings ? { background: `${btnBg}33` } : { background: 'hsl(var(--primary) / 0.1)' }}>
              <Bot className="h-5 w-5" style={settings ? { color: btnBg } : { color: 'hsl(var(--primary))' }} />
            </div>
            <div>
              <p className="font-semibold text-sm" style={textStyle}>{agentName}</p>
              <p className="text-xs opacity-60" style={textStyle}>Informe seus dados para iniciar</p>
            </div>
          </div>

          <form onSubmit={handleStartChat} className="px-6 py-5 space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium opacity-80" style={textStyle} htmlFor="pc-name">
                Nome <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <UserCircle2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-40" style={textStyle} />
                <input id="pc-name" type="text" required value={visitorName} onChange={e => setVisitorName(e.target.value)}
                  placeholder="Seu nome" maxLength={80}
                  className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 placeholder:opacity-40"
                  style={{ ...inputStyle, '--tw-ring-color': `${btnBg}66` } as React.CSSProperties} />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium opacity-80" style={textStyle} htmlFor="pc-email">
                E-mail <span className="opacity-50 text-[0.7rem]">(opcional)</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-40" style={textStyle} />
                <input id="pc-email" type="email" value={visitorEmail} onChange={e => setVisitorEmail(e.target.value)}
                  placeholder="seu@email.com" maxLength={120}
                  className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 placeholder:opacity-40"
                  style={{ ...inputStyle, '--tw-ring-color': `${btnBg}66` } as React.CSSProperties} />
              </div>
            </div>
            <button type="submit" disabled={!visitorName.trim()}
              className="w-full py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
              style={settings ? { background: btnBg, color: btnText } : { background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' }}>
              Iniciar conversa
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // ── Embed mode — lightweight layout for iframe embedding ──────────────────
  if (embedMode) {
    return (
      <div className="flex flex-col h-screen bg-background text-foreground">
        {/* Header */}
        <header className="px-4 py-3.5 flex items-center gap-3 shrink-0 border-b border-border bg-card">
          <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0 bg-primary/10">
            <Bot className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold leading-tight truncate">{agentName}</h1>
            <p className="text-xs text-muted-foreground">
              {isLoading ? 'Digitando…' : isLocked ? 'Conversa encerrada' : 'Online'}
            </p>
          </div>
        </header>
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
          {welcomeMessage && messages.length === 0 && (
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-primary/10">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div className="max-w-[80%] rounded-2xl rounded-tl-sm px-4 py-3 bg-muted">
                <MarkdownRenderer variant="chat" className="text-inherit">
                  {welcomeMessage}
                </MarkdownRenderer>
              </div>
            </div>
          )}
          <AnimatePresence initial={false}>
            {messages.map((msg, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                {msg.role === 'assistant' && (
                  <div className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-primary/10">
                    <Bot className="h-4 w-4 text-primary" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
                  msg.role === 'user' ? 'rounded-tr-sm bg-primary text-primary-foreground whitespace-pre-wrap break-words' : 'rounded-tl-sm bg-muted'
                }`}>
                  {msg.role === 'assistant' ? (
                    <MarkdownRenderer variant="chat">
                      {msg.content}
                    </MarkdownRenderer>
                  ) : msg.content}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
          {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 bg-primary/10">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div className="rounded-2xl rounded-tl-sm px-4 py-3 bg-muted">
                <div className="flex gap-1">
                  {[0,150,300].map(d => <span key={d} className="h-2 w-2 rounded-full bg-muted-foreground/50 animate-bounce motion-reduce:animate-none" style={{ animationDelay: `${d}ms` }} />)}
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        {/* Input */}
        <div className="px-4 py-3.5 shrink-0 border-t border-border bg-card">
          {isLocked ? (
            <div className="flex flex-col items-center gap-2">
              <p className="text-xs text-muted-foreground">Esta conversa foi encerrada.</p>
              <button onClick={startNewConversation} className="flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
                <PlusCircle className="h-3.5 w-3.5" /> Nova conversa
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input value={input} onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                placeholder="Digite uma mensagem…" disabled={isLoading || isLocked}
                className="flex-1 h-11 sm:h-10" maxLength={2000} />
              <Button onClick={handleSend} disabled={isLoading || isLocked || !input.trim()} size="icon"
                className="h-11 w-11 sm:h-10 sm:w-10 shrink-0">
                <Send className="h-5 w-5 sm:h-4 sm:w-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Main chat ────────────────────────────────────────────────────────────────
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.12 } }}
      transition={{ duration: 0.22, ease: 'easeOut' }}
      className="relative overflow-hidden flex flex-col min-h-[100dvh]"
    >
      <SeoHead title={`Chat with ${agentName}`} description={`Chat with ${agentName} powered by AI`} path={`/chat/${agentId}`} noindex />

      {/* Background layer — always full-page */}
      {isLiquid && <div className="absolute inset-0"><LiquidIridescentBackground /></div>}
      {isWebGLIrid && <div className="absolute inset-0"><WebGLIridescentBackground /></div>}
      {isAurora && <div className="absolute inset-0"><WebGLAuroraBorealisBackground /></div>}
      {isPortal && <div className="absolute inset-0"><WebGLPortalBackground /></div>}
      {isPool && <div className="absolute inset-0"><WebGLPoolBackground /></div>}
      {isCyber && <div className="absolute inset-0"><WebGLCyberfluidBackground /></div>}
      {isMarble && <div className="absolute inset-0"><WebGLLiquidMarbleBackground variant={marbleVariant} /></div>}
      {isMesh && <div className="absolute inset-0"><WebGLMeshGradientBackground variant={meshVariant} /></div>}
      {isVideo && settings?.background_video_url && (
        <div className="absolute inset-0">
          <VideoBackground url={settings.background_video_url} />
          {settings.background_overlay_enabled && (
            <div className="absolute inset-0" style={{ background: getVideoOverlayStyle(settings) ?? undefined }} />
          )}
        </div>
      )}
      {showCssBg && settings && (
        <div className={`absolute inset-0 ${presetAnim ?? ''}`} style={{ background: bgStyle }} />
      )}

      {/* Content column */}
      <div className="relative z-10 flex flex-col flex-1 sm:items-center sm:justify-center sm:p-4">
        <div className="w-full sm:max-w-md flex flex-col flex-1 sm:flex-none">

          {/* Profile strip — owner identity above the chat */}
          {!embedMode && (
            <div className="flex items-center gap-2.5 px-3 py-3 shrink-0">
              <button
                onClick={() => ownerUsername ? navigate(`/${ownerUsername}`) : navigate('/')}
                title="Voltar para a bio"
                className="h-11 w-11 sm:h-9 sm:w-9 -ml-1 sm:ml-0 rounded-full flex items-center justify-center shrink-0 opacity-60 hover:opacity-100 transition-opacity"
                style={textStyle}
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              {ownerAvatarUrl && !ownerAvatarError ? (
                <img src={ownerAvatarUrl} alt={ownerDisplayName ?? ''} className="h-10 w-10 rounded-full object-cover shrink-0 ring-1 ring-black/5" onError={() => setOwnerAvatarError(true)} />
              ) : (
                <div className="h-10 w-10 rounded-full flex items-center justify-center shrink-0 ring-1 ring-black/5"
                  style={settings ? { background: `${btnBg}33` } : { background: 'hsl(var(--primary) / 0.1)' }}>
                  <Bot className="h-5 w-5" style={settings ? { color: btnBg } : { color: 'hsl(var(--primary))' }} />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm leading-tight truncate tracking-tight" style={textStyle}>{ownerDisplayName || agentName}</p>
                {ownerBio && <p className="text-xs opacity-60 truncate leading-snug" style={textStyle}>{ownerBio}</p>}
              </div>
            </div>
          )}

          {/* Chat card */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.28, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="flex-1 flex flex-col overflow-hidden sm:flex-none sm:h-[min(78vh,640px)] sm:rounded-2xl sm:shadow-2xl"
          >
            <div className="flex flex-col h-full">

      {/* Header — chatbot identity */}
      <header
        className="px-4 py-3.5 flex items-center gap-3 shrink-0 sticky top-0 z-10"
        style={settings ? glassHeader : { borderBottom: '1px solid hsl(var(--border))', background: 'hsl(var(--card))' }}
      >
        {/* Bot icon */}
        <div
          className="h-8 w-8 rounded-full flex items-center justify-center shrink-0"
          style={settings ? { background: `${btnBg}33` } : { background: 'hsl(var(--primary) / 0.1)' }}
        >
          <Bot className="h-4 w-4" style={settings ? { color: btnBg } : { color: 'hsl(var(--primary))' }} />
        </div>

        {/* Agent name + status */}
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-sm leading-tight tracking-[-0.01em] truncate" style={textStyle}>{agentName}</h2>
          <p className="text-xs opacity-60" style={textStyle}>
            {isLoading ? 'Digitando...' : isLocked ? 'Conversa encerrada' : 'Online'}
          </p>
        </div>

        {/* End conversation */}
        {!isLocked && (
          <button
            onClick={() => setShowEndConfirm(true)}
            title="Encerrar conversa"
            className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 opacity-50 hover:opacity-100 transition-opacity"
            style={textStyle}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
        {welcomeMessage && messages.length === 0 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex gap-3">
            <div className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
              style={settings ? { background: `${btnBg}22` } : { background: 'hsl(var(--primary) / 0.1)' }}>
              <Bot className="h-4 w-4" style={settings ? { color: btnBg } : { color: 'hsl(var(--primary))' }} />
            </div>
              <div className="max-w-[80%] rounded-2xl rounded-tl-sm px-4 py-3"
              style={settings ? assistantBubble : { background: 'hsl(var(--muted))' }}>
              <MarkdownRenderer variant="chat" className="text-inherit">
                {welcomeMessage}
              </MarkdownRenderer>
            </div>
          </motion.div>
        )}

        <AnimatePresence initial={false}>
          {messages.map((msg, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
              {msg.role === 'assistant' && (
                <div className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={settings ? { background: `${btnBg}22` } : { background: 'hsl(var(--primary) / 0.1)' }}>
                  <Bot className="h-4 w-4" style={settings ? { color: btnBg } : { color: 'hsl(var(--primary))' }} />
                </div>
              )}
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${msg.role === 'user' ? 'rounded-tr-sm' : 'rounded-tl-sm'}`}
                style={msg.role === 'user'
                  ? (userBubbleStyle ?? { background: 'hsl(var(--primary))', color: 'hsl(var(--primary-foreground))' })
                  : (settings ? assistantBubble : { background: 'hsl(var(--muted))' })}>


                {msg.role === 'assistant' ? (
                  <MarkdownRenderer variant="chat" className="text-inherit">
                    {msg.content}
                  </MarkdownRenderer>
                ) : (
                  <p className="text-sm leading-[1.55] whitespace-pre-wrap break-words">{msg.content}</p>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
          <div className="flex gap-3">
            <div className="h-8 w-8 rounded-full flex items-center justify-center shrink-0"
              style={settings ? { background: `${btnBg}22` } : { background: 'hsl(var(--primary) / 0.1)' }}>
              <Bot className="h-4 w-4" style={settings ? { color: btnBg } : { color: 'hsl(var(--primary))' }} />
            </div>
            <div className="rounded-2xl rounded-tl-sm px-4 py-3"
              style={settings ? assistantBubble : { background: 'hsl(var(--muted))' }}>
              <div className="flex gap-1">
                <span className="h-2 w-2 rounded-full animate-bounce motion-reduce:animate-none" style={{ background: settings ? `${settings.text_color}99` : 'hsl(var(--muted-foreground) / 0.5)', animationDelay: '0ms' }} />
                <span className="h-2 w-2 rounded-full animate-bounce motion-reduce:animate-none" style={{ background: settings ? `${settings.text_color}99` : 'hsl(var(--muted-foreground) / 0.5)', animationDelay: '150ms' }} />
                <span className="h-2 w-2 rounded-full animate-bounce motion-reduce:animate-none" style={{ background: settings ? `${settings.text_color}99` : 'hsl(var(--muted-foreground) / 0.5)', animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input / locked footer */}
      <div className="px-4 py-3.5 pb-[max(0.875rem,env(safe-area-inset-bottom))] shrink-0 sticky bottom-0" style={settings ? glassFooter : { borderTop: '1px solid hsl(var(--border))', background: 'hsl(var(--card))' }}>
        {isLocked ? (
          <div className="flex flex-col items-center gap-2">
            <p className="text-xs opacity-60" style={textStyle}>Esta conversa foi encerrada.</p>
            <button onClick={() => { startNewConversation(); if (ownerUsername) navigate(`/${ownerUsername}`); }}
              className="flex items-center gap-1.5 text-xs font-semibold hover:underline transition-opacity"
              style={{ color: btnBg ?? 'hsl(var(--primary))' }}>
              <PlusCircle className="h-3.5 w-3.5" />
              Nova conversa
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder="Digite uma mensagem..."
              disabled={isLoading || isLocked}
              className="flex-1 h-11 sm:h-10 placeholder:opacity-60 focus-visible:ring-1"
              style={inputStyle}
              maxLength={2000}
            />
            <Button onClick={handleSend} disabled={isLoading || isLocked || !input.trim()} size="icon"
              style={sendBtnStyle} className={cn('h-11 w-11 sm:h-10 sm:w-10 shrink-0', sendBtnStyle && 'border-0')}>
              <Send className="h-5 w-5 sm:h-4 sm:w-4" />
            </Button>
          </div>
        )}
        <p className="text-[10px] opacity-30 text-center mt-2" style={textStyle}>
          Powered by AI • Respostas podem não ser precisas
        </p>
      </div>

            </div>{/* flex flex-col h-full */}
          </motion.div>{/* chat card */}

        </div>{/* sm:max-w-md column */}
      </div>{/* z-10 content column */}

      {/* End conversation confirm */}
      <AlertDialog open={showEndConfirm} onOpenChange={setShowEndConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Encerrar conversa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta conversa será encerrada e você será redirecionado de volta.
              {messages.length === 0 && ' Nenhuma mensagem foi enviada.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmEnd}>
              Sim, encerrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
